import * as React from 'react';
import { actions, Control, Errors, LocalForm } from 'react-redux-form';

import { DynamicSelectIdentifier, Select, CheckboxInline } from '@common/components';
import { getDefaultErrorMessages, hasValue } from '@common/validators';
import { SavedCreditCard } from '@models/Account';
import { ContractOrderType } from '@models/Contract';
import { PaymentMethods } from '@models/HostedCheckoutTransactionViewModel';
import { OptionsViewModel } from '@models/common.models';
import { PaymentTerms, PaymentType, PaymentViewModel, PaymentPurpose } from '@models/Payment';

import { HostedCheckout } from './hosted-checkout-container';
import { PaymentACHComponent } from '../payment-ach.component';
import { PaymentCreditCard } from '../payment-credit-card.component';
import { PaymentTypeFormControlsComponent } from '../payment-type-form-controls.component';

const ESCROW_PAYMENT_ID = 14;

export const initialPaymentViewModel: PaymentViewModel = {
  paymentTerms: PaymentTerms.OneInstallment,
  paymentTypeBalance: PaymentType.CreditCard,
  paymentTypeInitial: PaymentType.CreditCard
};

export interface PaymentComponentProperties {
  readonly affiliatedAccounts?: OptionsViewModel[];
  readonly contractEndDate?: Date;
  readonly contractId: number;
  readonly defaultSavedCreditCard?: SavedCreditCard;
  readonly installmentsFee: number;
  readonly isBalancePayment?: boolean;
  readonly isDemandLetterDefault?: boolean;
  readonly isDemandLetterEnabled?: boolean;
  readonly isPaymentDateInvalid: boolean;
  readonly maxPaymentAmountInitial?: number;
  readonly orderType?: ContractOrderType;
  readonly paymentAmountBalance: number;
  readonly paymentAmountInitial?: number;
  readonly paymentDate?: Date;
  readonly paymentPerTermString: string;
  readonly paymentPurpose: PaymentPurpose;
  readonly paymentTerms: PaymentTerms;
  readonly paymentTypeBalance: PaymentType;
  readonly paymentTypeInitial: PaymentType;
  readonly remainingBalance: number;
  readonly remainingBalanceDueString: string;
  readonly savedCreditCardBalance?: SavedCreditCard;
  readonly savedCreditCardInitial?: SavedCreditCard;
  readonly savedCreditCards?: Array<SavedCreditCard>;
  readonly totalBalanceDueString: string;
  readonly totalPrice?: number;

  readonly setPaymentAmountInitial: (paymentAmount: number | null) => void;
  readonly setPaymentDate: (paymentDate: Date, contractEndDate?: Date) => void;
  readonly setPaymentTerms: (paymentTerms: PaymentTerms) => void;
  readonly setPaymentType: (paymentType: PaymentType, isBalance: boolean) => void;
  readonly setSavedCreditCard: (creditCard: SavedCreditCard | undefined, isBalance: boolean) => void;
  readonly submit: (
    model: PaymentViewModel,
    copyInitialPaymentDetails: boolean,
    savedCreditCardInitial?: SavedCreditCard
  ) => void;
  readonly submitButtonRef?: (button: HTMLButtonElement) => void;
}

interface PaymentComponentState {
  copyInitialPaymentDetails: boolean;
  formDispatcher?: Function;
  isInitialized: boolean;
}

export class PaymentComponent extends React.PureComponent<PaymentComponentProperties, PaymentComponentState> {
  button?: HTMLButtonElement;

  constructor(props: PaymentComponentProperties) {
    super(props);
    this.state = {
      copyInitialPaymentDetails: false,
      isInitialized: false
    };
  }

  public componentDidMount(): void {
    if (this.props.isDemandLetterDefault) {
      this.props.setPaymentType(PaymentType.DemandLetter, false);
    }
  }

  public componentDidUpdate(): void {
    var completeButton = document.getElementById('wizardNextButton');
    if (this.props.paymentTypeInitial === PaymentType.CreditCard) {
      if (completeButton && completeButton.firstChild) {
        completeButton.firstChild.nodeValue = 'Proceed';
      }
    } else {
      if (completeButton && completeButton.firstChild) {
        completeButton.firstChild.nodeValue = 'Complete';
      }
    }
  }

  public componentWillUpdate(nextProps: Readonly<PaymentComponentProperties>): void {
    if (this.state.formDispatcher == null) {
      return;
    }

    if (nextProps.paymentAmountInitial) {
      this.state.formDispatcher(
        actions.change('paymentForm.paymentAmountInitial', nextProps.paymentAmountInitial!.toFixed(2))
      );
    }
    this.state.formDispatcher(actions.change('paymentForm.paymentTerms', nextProps.paymentTerms));
    this.state.formDispatcher(
      actions.change(
        'paymentForm.paymentTypeBalance',
        nextProps.paymentTerms === PaymentTerms.OneInstallment ? null : nextProps.paymentTypeBalance
      )
    );
    this.state.formDispatcher(actions.change('paymentForm.paymentTypeInitial', nextProps.paymentTypeInitial));

    if (this.props.isDemandLetterDefault) {
      this.state.formDispatcher(
        actions.change('paymentForm.paymentDemandLetterInitial.contractBillTo', ESCROW_PAYMENT_ID)
      );
    }
  }

  public render(): JSX.Element {
    if (!this.props.totalPrice) {
      return <h4>Please assign coverage to new contract.</h4>;
    }

    // I had this in componentDidMount(), but it didn't work there
    // if this is moved to componentDidMount(), this state.isInitialized thing is no longer needed
    if (!this.state.isInitialized) {
      if (this.props.paymentAmountInitial) {
        this.setState({ isInitialized: true });
      } else {
        this.props.setPaymentAmountInitial(this.props.totalPrice);
      }
    }

    return (
      <div className="light-gray-bc">
        <LocalForm
          getDispatch={d => this.setState({ formDispatcher: d })}
          hideNativeErrors={false}
          initialState={initialPaymentViewModel}
          model="paymentForm"
          onSubmit={m => {
            if (this.props.paymentTypeInitial === PaymentType.CreditCard) {
              if (!this.props.savedCreditCardInitial && this.button) {
                // trigger the submission of the checkout launch component
                this.button.click();
                return;
              }
            }
            return this.props.submit(
              { ...m, paymentDate: this.props!.paymentDate },
              this.state.copyInitialPaymentDetails,
              this.props.savedCreditCardInitial
            );
          }}
        >
          <div className="row">
            <div className={this.props.paymentTerms === PaymentTerms.OneInstallment ? 'col-sm-12' : 'col-xl-6'}>
              <h3>Payment Type</h3>
              <div className="row">
                <div className="col-lg-3">
                  <PaymentTypeFormControlsComponent
                    isDemandLetterEnabled={
                      this.props.paymentPurpose === PaymentPurpose.Contract && !!this.props.isDemandLetterEnabled
                    }
                    modelPathPrefix="paymentForm.paymentTypeInitial"
                    paymentType={this.props.paymentTypeInitial}
                    setPaymentType={pt => this.props.setPaymentType(pt, false)}
                    isNegativePayment={this.props.totalPrice && this.props.totalPrice < 0 ? true : false}
                    isBalancePayment={this.props.isBalancePayment}
                  />
                  {this.props.orderType === ContractOrderType.RealtyEscrow &&
                    !!this.props.isDemandLetterEnabled && (
                      <div className="form-control mt-25">
                        <CheckboxInline className="end-xs">
                          <Control.checkbox model=".doNotSendDemandLetter" />
                          <label htmlFor="paymentForm.doNotSendDemandLetter">Do Not Send Demand Letter</label>
                        </CheckboxInline>
                      </div>
                    )}
                </div>

                <div className="col-lg-9">
                  <div className="row">
                    {/* Hide this from users for now, as it does not work w/ hosted checkout yet.
                    <div className="col-lg-6 form-control">
                      <label>Payment Date</label>
                      <ReactDatePicker
                        className={this.props.isPaymentDateInvalid ? 'is-invalid' : ''}
                        onChange={m => m && this.props.setPaymentDate(m.toDate(), this.props.contractEndDate)}
                        selected={this.props.paymentDate && dateParser.parse(this.props.paymentDate)}
                        value={this.props.paymentDate && dateParser.parse(this.props.paymentDate).format('MM/DD/YYYY')}
                      />
                      <div className="error">
                        <p>{this.props.isPaymentDateInvalid ? 'Please enter valid date' : ''}</p>
                      </div>
                  </div> */}
                    <div className="col-lg-6">{this.renderPaymentAmountInitialControl()}</div>
                  </div>

                  <div className="row">
                    {this.props.paymentPurpose !== PaymentPurpose.SWO &&
                      this.props.paymentPurpose !== PaymentPurpose.Multiple && (
                        <div className="col-lg-6">{this.renderPaymentTermsFormControl()}</div>
                      )}
                    <div className="col-lg-6 form-control">
                      <label htmlFor="totalPrice">Total Balance Due</label>
                      <h2>{`$${(this.props.totalPrice + this.props.installmentsFee).toFixed(2)}`}</h2>
                      <label>
                        {this.props.paymentPurpose !== PaymentPurpose.Multiple && this.props.totalBalanceDueString}
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <h3>Payment Information</h3>
              {this.renderPaymentFormControls(true, this.props.paymentTypeInitial)}
            </div>

            {this.props.paymentTerms !== PaymentTerms.OneInstallment && (
              <div className="col-xl-6">
                <h3>Balance Payment Type</h3>
                <div className="row">
                  <div className="col-lg-3">
                    <PaymentTypeFormControlsComponent
                      isDemandLetterEnabled={
                        this.props.paymentPurpose === PaymentPurpose.Contract && !!this.props.isDemandLetterEnabled
                      }
                      modelPathPrefix="paymentForm.paymentTypeBalance"
                      paymentType={this.props.paymentTypeBalance}
                      setPaymentType={pt => this.props.setPaymentType(pt, true)}
                      isNegativePayment={this.props.totalPrice && this.props.totalPrice < 0 ? true : false}
                    />
                  </div>
                  <div className="col-lg-9">
                    <div className="mb-10">
                      <label htmlFor="remainingBalanceDue">
                        Remaining Balance Due ( {this.props.remainingBalanceDueString} )
                      </label>
                      <input
                        disabled={true}
                        id="remainingBalanceDue"
                        type="text"
                        value={this.props.remainingBalance.toFixed(2)}
                      />
                    </div>
                    <label className="mt-5" htmlFor="paymentPerTerm">
                      Payment Per Term ( {this.props.paymentPerTermString} )
                    </label>
                    <input
                      disabled={true}
                      id="paymentPerTerm"
                      type="text"
                      value={this.props.paymentAmountBalance.toFixed(2)}
                    />
                  </div>
                </div>

                {this.props.paymentTypeBalance === PaymentType.DemandLetter && (
                  <div>
                    <h3>Balance Payment Information</h3>
                    {this.renderPaymentFormControls(false, this.props.paymentTypeBalance)}
                  </div>
                )}
              </div>
            )}
          </div>
          {this.renderSubmitButton(this.props.submitButtonRef)}
        </LocalForm>
        {this.props.paymentTypeInitial === PaymentType.CreditCard && (
          <HostedCheckout
            {...this.props}
            getHiddenFormButton={this.loadButton}
            contractId={this.props.contractId}
            paymentMethod={PaymentMethods.CreditCard}
          />
        )}
      </div>
    );
  }

  private handlePaymentTermsChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    const optionValue: string = event.currentTarget.value;
    if (!optionValue || optionValue === '') {
      alert('Tried to set invalid payment terms.');
    }

    const paymentTerms: number = parseInt(optionValue, 10) as PaymentTerms;
    if (paymentTerms === PaymentTerms.OneInstallment) {
      this.state.formDispatcher!(actions.change('paymentForm.paymentTypeBalance', undefined));
    }
    this.props.setPaymentTerms(paymentTerms);
  };

  private renderPaymentAmountInitialControl(): JSX.Element {
    let controlProps = {
      defaultValue: (this.props.paymentAmountInitial || this.props.totalPrice!).toFixed(2),
      disabled: this.props.maxPaymentAmountInitial != null ? undefined : true,
      id: 'paymentForm.paymentAmountInitial',
      min: 0.01,
      model: 'paymentForm.paymentAmountInitial',
      onChange: (event: React.FormEvent<HTMLInputElement>) => {
        const input: number = parseFloat(event.currentTarget.value);
        if ((input || input === 0) && !isNaN(input)) {
          this.props.setPaymentAmountInitial(input);
        } else {
          this.props.setPaymentAmountInitial(null);
        }
      },
      max: this.props.maxPaymentAmountInitial != null ? this.props.maxPaymentAmountInitial! : undefined,
      parser: (value: string) => parseFloat(value),
      step: 0.01,
      type: 'number'
    };

    if (this.props.paymentTerms === PaymentTerms.OneInstallment) {
      let valuePropsName = 'value';
      controlProps[valuePropsName] = (this.props.paymentAmountInitial || this.props.totalPrice!).toFixed(2);
    } else {
      let validatorsPropsName = 'validators';
      controlProps[validatorsPropsName] = {
        range: (value: number) => value <= this.props.maxPaymentAmountInitial! && value > 0.0
      };
    }

    return (
      <div className="form-control">
        <label htmlFor="paymentForm.paymentAmountInitial">
          {this.props.paymentPurpose === PaymentPurpose.SWO || this.props.paymentPurpose === PaymentPurpose.Multiple
            ? ''
            : 'Initial'}{' '}
          Payment Amount
        </label>
        <Control.text {...controlProps} />
        <Errors
          className="error"
          messages={getDefaultErrorMessages({ range: 'Invalid payment amount' })}
          model="paymentForm.paymentAmountInitial"
          show={{ touched: true, focus: false }}
        />
      </div>
    );
  }

  private renderPaymentFormControls(isInitialPayment: boolean, paymentType: PaymentType): JSX.Element | null {
    let modelPathPrefix: string = 'paymentForm.';
    if (paymentType === PaymentType.ACH) {
      modelPathPrefix = modelPathPrefix.concat(isInitialPayment ? 'paymentACHInitial' : 'paymentACHBalance');
      return <PaymentACHComponent modelPathPrefix={modelPathPrefix} />;
    } else if (paymentType === PaymentType.CreditCard && this.state.formDispatcher) {
      modelPathPrefix = modelPathPrefix.concat(
        isInitialPayment ? 'paymentCreditCardInitial' : 'paymentCreditCardBalance'
      );
      if (isInitialPayment || !this.state.copyInitialPaymentDetails) {
        return (
          <PaymentCreditCard
            savedCreditCard={isInitialPayment ? this.props.savedCreditCardInitial : this.props.savedCreditCardBalance}
            savedCreditCards={this.props.savedCreditCards || []}
            setSavedCreditCard={(cc: SavedCreditCard | undefined) =>
              this.props.setSavedCreditCard(cc, !isInitialPayment)
            }
          />
        );
      }
      if (!isInitialPayment) {
        return null;
      }
    } else if (paymentType === PaymentType.DemandLetter) {
      modelPathPrefix = isInitialPayment
        ? 'paymentForm.paymentDemandLetterInitial'
        : 'paymentForm.paymentDemandLetterBalance';
      return (
        <div className="form-control">
          <label>{isInitialPayment ? '1st Billing' : 'Later Bills'}</label>
          <Select
            model={modelPathPrefix.concat('.contractBillTo')}
            identifier={DynamicSelectIdentifier.BillingOptions}
            validators={{ required: hasValue }}
          />
          <Errors
            className="error"
            messages={getDefaultErrorMessages()}
            model={modelPathPrefix.concat('.contractBillTo')}
            show={{ touched: true, focus: false }}
          />
        </div>
      );
    }

    return <></>;
  }

  private renderPaymentTermsFormControl(): JSX.Element {
    return (
      <div className="form-control">
        <label htmlFor="paymentForm.paymentTerms">Payment Terms</label>
        <Control.select
          id="paymentForm.paymentTerms"
          onChange={this.handlePaymentTermsChange}
          model="paymentForm.paymentTerms"
          disabled={this.props.paymentTypeInitial === PaymentType.DemandLetter}
          value={this.props.paymentTerms.toString()}
        >
          <option value={PaymentTerms.OneInstallment.toString()}>Pay in full</option>
          <option value={PaymentTerms.TwoInstallments.toString()}>2 Payments</option>
        </Control.select>
      </div>
    );
  }

  private renderSubmitButton(buttonRef?: (button: HTMLButtonElement) => void): JSX.Element {
    if (buttonRef != null) {
      return (
        <button className="btn btn-hidden" type="submit" ref={this.props.submitButtonRef}>
          Submit
        </button>
      );
    }
    return (
      <button className="btn btn-primary" type="submit">
        Submit
      </button>
    );
  }

  private loadButton = (b: any): void => {
    this.button = b;
  };
}



// WEBPACK FOOTER //
// ./src/payment/_components/gateway/hosted-payment.component.tsx